import { useState } from 'react';
import Button from '@/components/base/Button';
import { api } from '@/utils/api';
import type { CampaignBrief } from '../types';
import { briefPatch, type ExtractedBrief } from '../extractedBrief';

interface BriefExtractorProps {
  /** Fills the brief (and the contract text, when the brief states terms). */
  onApply: (patch: Partial<CampaignBrief>, terms: string | null) => void;
}

/** The live create page's "Paste a brief and AI fills the form" (POST campaigns/extract-brief). */
export default function BriefExtractor({ onApply }: BriefExtractorProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');

  const extract = async () => {
    setBusy(true);
    setError('');
    setResult('');
    try {
      const { fields } = await api<{ fields: ExtractedBrief }>('/campaigns/extract-brief', {
        method: 'POST',
        body: JSON.stringify({ brief: text.trim() }),
      });
      const patch = briefPatch(fields);
      onApply(patch, fields.terms);
      const filled = Object.keys(patch).length + (fields.terms ? 1 : 0);
      const perCreator =
        fields.budget_per_creator !== null && fields.budget_per_creator !== undefined
          ? ` The brief gives ${fields.budget_currency ?? ''} ${fields.budget_per_creator} per creator: set the overall budget once you know how many creators you'll invite.`
          : '';
      setResult(
        filled > 0
          ? `Filled ${filled} field${filled === 1 ? '' : 's'}. Check each one before you continue.${perCreator}`
          : `The brief didn't state any of the form's fields.${perCreator}`,
      );
    } catch (extractError) {
      setError(extractError instanceof Error ? extractError.message : 'Could not read the brief.');
    } finally {
      setBusy(false);
    }
  };

  const tooShort = text.trim().length < 20;

  return (
    <section className="mb-5 rounded-lg border border-background-200 bg-background-50">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full cursor-pointer items-center justify-between gap-3 px-5 py-3.5 text-left"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-sparkling-2-line" />
          </span>
          <span>
            <span className="block text-sm font-medium text-foreground-900">Paste a brief</span>
            <span className="block text-xs text-foreground-500">AI fills the form from an email, document or notes</span>
          </span>
        </span>
        <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg text-foreground-500`} />
      </button>
      {open ? (
        <div className="space-y-3 border-t border-background-200 px-5 py-4">
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            rows={6}
            maxLength={10000}
            placeholder="Paste your campaign brief here — email, document, notes, anything…"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[11px] text-foreground-400">
              {tooShort ? 'At least 20 characters.' : `${text.trim().length}/10000`} Anything not in the brief stays as it is.
            </p>
            <Button size="sm" icon="ri-magic-line" disabled={tooShort || busy} onClick={() => void extract()}>
              {busy ? 'Reading the brief…' : 'Fill the form'}
            </Button>
          </div>
          {error ? (
            <p role="alert" className="flex items-start gap-1.5 text-xs text-primary-700">
              <i className="ri-error-warning-line mt-0.5" />
              {error}
            </p>
          ) : null}
          {result ? (
            <p className="flex items-start gap-1.5 text-xs text-accent-800">
              <i className="ri-checkbox-circle-line mt-0.5" />
              {result}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
